export const PROJECT_TYPES = Object.freeze({
  AGRO_DRAFT: "agro_draft",
  CARBON_DRAFT: "carbon_draft",
  AGRO_UNAPPROVED: "agro_unapproved",
  CARBON_UNAPPROVED: "carbon_unapproved",
  AGROFORESTRY: "agroforestry",
  CARBON_CREDIT: "carboncredit",
});

import flagEN from "./assets/flag-en.png";
import flagJA from "./assets/flag-ja.png";
import flagBR from "./assets/flag-br.png";

import tree from "./assets/activities/tree.svg";
import star from "./assets/activities/star.svg";
import fire from "./assets/activities/fire.svg";
import shield from "./assets/activities/shield.svg";
import smile from "./assets/activities/smile.svg";
import wheat from "./assets/activities/wheat.svg";
import leaves from "./assets/activities/leaves.svg";
import drop from "./assets/activities/drop.svg";

import carbonSeq from "./assets/impact/carbon-sequestration.svg";
import fauna from "./assets/impact/fauna.svg";
import jobsCreated from "./assets/impact/jobs-created.svg";
import waterAvailability from "./assets/impact/water-availability.svg";
import soilLife from "./assets/impact/soil-life.svg";
import cropDiversity from "./assets/impact/crop-diversity.svg";
import conversionLand from "./assets/impact/conversion-land.svg";
import regenerativeFood from "./assets/impact/regenerative-food.svg";
import riverBank from "./assets/impact/river-banks.svg";

import acai from "./assets/species/acai.svg";
import banana from "./assets/species/banana.svg";
import black_papper from "./assets/species/black_papper.svg";
import brazil_nuts from "./assets/species/brazil_nuts.svg";
import cacava from "./assets/species/cacava.svg";
import cocoa from "./assets/species/cocoa.svg";
import coconut from "./assets/species/coconut.svg";
import coffee from "./assets/species/coffee.svg";
import lime from "./assets/species/lime.svg";
import mango from "./assets/species/mango.svg";
import orange from "./assets/species/orange.svg";
import papaya from "./assets/species/papaya.svg";
import wood from "./assets/species/wood.svg";
import carbon from "./assets/species/carbon.svg";
import cupuacu from "./assets/species/cupuacu.svg";
import usdc from "./assets/species/usdc.svg";
import {
  getSpeciesAliases,
  normalizeSpeciesInputKey,
  SPECIES_CATALOG_METADATA,
  SPECIES_KEY_ALIASES,
} from "./utils/speciesCatalog";

import metamaskIcon from "./assets/wallets/metamask-logo.webp";
import phantomIcon from "./assets/wallets/phantom-logo.webp";
import braveIcon from "./assets/wallets/brave-logo.webp";
import coinbaseIcon from "./assets/wallets/coinbase-logo.svg";
import trustIcon from "./assets/wallets/trust-wallet-logo.svg";
import rabbyIcon from "./assets/wallets/rabby-logo.webp";
import argentIcon from "./assets/wallets/argent-logo.webp";
import atomicIcon from "./assets/wallets/atomic-logo.webp";
import binanceIcon from "./assets/wallets/binance-logo.webp";
import exodusIcon from "./assets/wallets/exodus-logo.webp";
import okxIcon from "./assets/wallets/okx-logo.webp";

export const CARBON_PROJECT_ENDPOINT = "projects-carbon";
export const AGROFORESTRY_PROJECT_ENDPOINT = "projects-agroforestry";
export const PROJECTS_ENDPOINT = "projects";
export const DRAFT_ENDPOINT = "drafts";
export const OFFERING_ENDPOINT = "offering";
export const PHOTOS_ENDPOINT = "photos";

export const REGISTERED_PROJECTS_ENDPOINT = "registered-projects";
export const TRANSACTIONS_ENDPOINT = "transactions";
export const INVESTORS_ENDPOINT = "investors";
export const FILES_ENDPOINT = "files";
export const USER_ENDPOINT = "user";
export const BLACKLIST_USER_ENDPOINT = "blacklist-user";
export const AML_CHECK_ENDPOINT = "check-aml";
export const VALIDATE_AML_ENDPOINT = "validate-aml";
export const SEND_EMAILS_ENDPOINT = "send_emails";
export const DEVELOPER_ENDPOINT = "developers";

export const AUDITOR_ENDPOINT = "auditors";
export const FUTURE_USERS_ENDPOINT = "future-users";
export const FUTURE_USER_RETURN_ENDPOINT = "future-users-return";
export const FUTURE_USERS_ENDPOINT_REMOVE_FROM_PROJECT =
  "remove-future-user-from-project";
export const FUTURE_USERS_APPROVE_ENDPOINT = "future-users-approve";
export const CHECK_AUTH = "check-auth";
export const TOKEN_DECIMALS = 2;
export const AUTHENTICATE_ENDPOINT = "authenticate";
export const DEVELOPER_AUTHENTICATE_ENDPOINT = "partner_authenticate";
export const AUTH_ROLES = Object.freeze({
  ADMIN: 0,
  DEVELOPER: 1,
  AUDITOR: 2,
  FARMER: 3,
  INVESTOR: 4,
});
export const DEVELOPER_WITHDRAW = "offering-partner-withdraw";
export const ADMIN_WITHDRAW = "offering-admin-withdraw";
export const OFFERING_PAUSE = "offering-pause";
/* Stats endpoints (Backend/server.py)
 * /carbon-stats         — approved+listed carbon-credit projects only
 * /agroforestry-stats   — approved agroforestry projects only
 * /combined-stats       — both, used by the homepage hectares banner
 * /main-projects-info   — deprecated alias for /carbon-stats, kept for back-compat
 */
export const CARBON_STATS = "carbon-stats";
export const AGROFORESTRY_STATS = "agroforestry-stats";
export const COMBINED_STATS = "combined-stats";
// Legacy aliases — keep imports working until we migrate every caller.
export const MAIN_PROJECTS_INFO = CARBON_STATS;
export const FARMER_PROJECTS_STATS = AGROFORESTRY_STATS;
export const ADMINS_ENDPOINT = "admins";
export const OWNERS_ENDPOINT = "owners";
export const CERTIFIERS_ENDPOINT = "certifiers";
export const BENEFITORS_ENDPOINT = "benefitors";
export const DEVELOPERS_ENDPOINT = "developers";
export const CARBON_DRAFTS_ENDPOINT = "drafts-carbon";
export const AGROFORESTRY_DRAFTS_ENDPOINT = "drafts-agroforestry";
export const DEVELOPER_OVERVIEW_ENDPOINT = "developer_overview";
export const PREDICT_YIELDS_ENDPOINT = "predict-agroforestry-yields";
export const UPLOAD_MAP_ENDPOINT = "upload-map";
export const UPLOAD_FIELD_PLOT_ENDPOINT = "upload-field-plot";
export const CARBON_CREDITS_FIELD_PLOT_ENDPOINT = "carbon-credits/field-plot";
export const CARBON_CREDITS_REMOTE_SENSING_ENDPOINT = "carbon-credits/remote-sensing";
export const CARBON_COSTS_AUTOFILL_ENDPOINT = "carbon-costs/autofill";
export const CALCULATE_STAGE_CARBON_ENDPOINT = "calculate-stage_carbon";
export const CARBON_PRICING_QUOTE_ENDPOINT = "carbon/pricing/quote";
export const CARBON_LISTING_PRICING_QUOTE_ENDPOINT =
  "carbon/pricing/quote-listing";

export const projectSchemaJsonKeys = [
  "id",
  "name",
  "state",
  "supply",
  "overview",
  "flora_count",
  "fauna_count",
  "endangered_species_count",
  "carbon_credit_amount",
  "project_funding_address",
  "developers",
  "auditor",
  "auditor_id",
  "offering",
  "project_file1_path",
  "project_file2_path",
  "forest_cover_photo_path",
  "project_photos_path",
  "project_state",
  "is_public",
  "listed",
  "pre_developer_carbon_credit_commission",
  "pre_developer_usd_commission",
  "created_by_id",
  "created_by",
  "project_type",
  "project_area_hectares",
  "certification_id",
  "project_certifier_links",
  "project_benefitor_links",
  "project_owner_links",
  "carbon_sequestration",
  "general_info",
  "personnel",
  "contractors",
  "supplies",
  "assets",
  "travel",
  "others",
  "activities",
  "yearly_costs",
  "carbon_credit_address",
  "smart_contract_address",
  "auditorSigners",
  "auditorThreshold",
  "tmp_auditors",
  "investment",
  "auditCost",
  "auditorFee",
  "token_name",
  "token_symbol",
  "developer",
  "developer_id",
  "auditorVerification",
  "auditorValidation",
  "version",
  "is_hidden",
  "ownersNew",
  "coBenefitors",
  "land_tenure_status",
  "project_activities",
  "year_number",
  "public_offering",
  "biome",
  "latitude",
  "longitude",
  "certification_report",
  "certification_body",
  "issuance_date",
  "project_file1_ipfs",
  "network_id",
];

export const publicOfferingJsonKeys = [
  "id",
  "project_id",
  "smart_contract_address",
  "sale_supply",
  "funding_address",
  "sale_year",
  "listing_price",
  "state",
];

export const offeringSchemaJsonKeys = [
  "id",
  "project_id",
  "network_id",
  "project_funding_address",
  "operator_address",
  "developer_address",
  "auditor_address",
  "supply",
  "listing_period_in_days",
  "offering_type",
  "offering_state",
  "smart_contract_address",
  "allocations",
  "users",
  "usd_commission",
  "developer_usd_commission",
  "carbon_credit_commission",
  "developer_carbon_credit_commission",
  "usd_commission_transfered",
  "carbon_credit_commission_transfered",
  "developer_usd_commission_transfered",
  "developer_carbon_credit_commission_transfered",
  "paused",
  "owners",
  "ownerThreshold",
  "adminThreshold",
  "developerThreshold",
  "ownerSigners",
  "adminSigners",
  "developerSigners",
];

export const futureUserSchemaJsonKeys = [
  "id",
  "business_name",
  "business_incorporation_date",
  "email",
  "wallet_address",
  "amount",
  "project_id",
  "project_name",
  "aml_check",
  "aml_date",
  "aml_status",
];

export const STATES_BRAZIL = [
  "Acre",
  "Alagoas",
  "Amapá",
  "Amazonas",
  "Bahia",
  "Ceará",
  "Distrito Federal",
  "Espírito Santo",
  "Goiás",
  "Maranhão",
  "Mato Grosso",
  "Mato Grosso do Sul",
  "Minas Gerais",
  "Pará",
  "Paraíba",
  "Paraná",
  "Pernambuco",
  "Piauí",
  "Rio de Janeiro",
  "Rio Grande do Norte",
  "Rio Grande do Sul",
  "Rondônia",
  "Roraima",
  "Santa Catarina",
  "São Paulo",
  "Sergipe",
  "Tocantins",
];

export const STATE_CODE_BY_NAME = Object.freeze({
  Acre: "AC",
  Alagoas: "AL",
  Amapá: "AP",
  Amazonas: "AM",
  Bahia: "BA",
  Ceará: "CE",
  "Distrito Federal": "DF",
  "Espírito Santo": "ES",
  Goiás: "GO",
  Maranhão: "MA",
  "Mato Grosso": "MT",
  "Mato Grosso do Sul": "MS",
  "Minas Gerais": "MG",
  Pará: "PA",
  Paraíba: "PB",
  Paraná: "PR",
  Pernambuco: "PE",
  Piauí: "PI",
  "Rio de Janeiro": "RJ",
  "Rio Grande do Norte": "RN",
  "Rio Grande do Sul": "RS",
  Rondônia: "RO",
  Roraima: "RR",
  "Santa Catarina": "SC",
  "São Paulo": "SP",
  Sergipe: "SE",
  Tocantins: "TO",
});

export const STATE_BIOMES = {
  Acre: ["0"],
  Alagoas: ["1", "3"],
  Amapá: ["0"],
  Amazonas: ["0"],
  Bahia: ["1", "2", "3"],
  Ceará: ["1", "3"],
  "Distrito Federal": ["2"],
  "Espírito Santo": ["3"],
  Goiás: ["2", "3"],
  Maranhão: ["0", "1", "2", "3"],
  "Mato Grosso": ["0", "2", "4"],
  "Mato Grosso do Sul": ["2", "4"],
  "Minas Gerais": ["1", "2", "3"],
  Pará: ["0"],
  Paraíba: ["1", "3"],
  Paraná: ["2", "3"],
  Pernambuco: ["1", "3"],
  Piauí: ["1", "2"],
  "Rio de Janeiro": ["3"],
  "Rio Grande do Norte": ["1", "3"],
  "Rio Grande do Sul": ["3", "5"],
  Rondônia: ["0", "2"],
  Roraima: ["0"],
  "Santa Catarina": ["3"],
  "São Paulo": ["2", "3"],
  Sergipe: ["1", "3"],
  Tocantins: ["0", "2"],
};

export const STATE_COORDINATES = {
  Acre: { lat: -9.0238, lng: -70.812 },
  Alagoas: { lat: -9.5713, lng: -36.782 },
  Amapá: { lat: 1.4155, lng: -51.3312 },
  Amazonas: { lat: -3.4168, lng: -65.8561 },
  Bahia: { lat: -12.5797, lng: -41.7007 },
  Ceará: { lat: -5.4984, lng: -39.3206 },
  "Distrito Federal": { lat: -15.7801, lng: -47.9292 },
  "Espírito Santo": { lat: -19.1834, lng: -40.3089 },
  Goiás: { lat: -15.827, lng: -49.8362 },
  Maranhão: { lat: -4.9609, lng: -45.2744 },
  "Mato Grosso": { lat: -12.6819, lng: -56.9211 },
  "Mato Grosso do Sul": { lat: -20.7722, lng: -54.7852 },
  "Minas Gerais": { lat: -18.5122, lng: -44.555 },
  Pará: { lat: -3.4168, lng: -52.3336 },
  Paraíba: { lat: -7.2399, lng: -36.7819 },
  Paraná: { lat: -24.4842, lng: -51.8623 },
  Pernambuco: { lat: -8.8137, lng: -36.9541 },
  Piauí: { lat: -7.7183, lng: -42.7289 },
  "Rio de Janeiro": { lat: -22.9068, lng: -43.1729 },
  "Rio Grande do Norte": { lat: -5.7945, lng: -36.599 },
  "Rio Grande do Sul": { lat: -30.0346, lng: -51.2177 },
  Rondônia: { lat: -11.5057, lng: -63.5806 },
  Roraima: { lat: 2.7376, lng: -62.0751 },
  "Santa Catarina": { lat: -27.5954, lng: -48.548 },
  "São Paulo": { lat: -23.5505, lng: -46.6333 },
  Sergipe: { lat: -10.5741, lng: -37.3857 },
  Tocantins: { lat: -10.1753, lng: -48.2982 },
};

export const investmentSchemaJsonKeys = [
  "id",
  "developer",
  "auditor",
  "certifier",
  "smart_contract_address",
  "project_id",
  "developer_credit_commission",
  "developer_fee",
  "project_failed",
  "project_completed",
  "stages",
  "yearly_costs",
  "auditorVerification",
  "auditorValidation",
];

export const PROJECT_IS_LIVE = 1;
export const PROJECT_ENDED = 2;

export const CHALLENGE_EXPIRED = "Challenge expired";

export const PENDING_REQUEST_MESSAGE = "pending_request";

export const DECIMAL_UNITS = 6;

export const ERC20_DECIMAL_UNITS = 18;

export const INFLATION_RATE = 0.045;

export const TESTING_ENV = "testing";
export const DEVELOPMENT_ENV = "development";
export const HARDHAT_CHAIN_ID = process.env.VUE_APP_CHAIN_ID
  ? parseInt(process.env.VUE_APP_CHAIN_ID, 10)
  : 31338;
const _activeChainId = process.env.VUE_APP_CHAIN_ID
  ? parseInt(process.env.VUE_APP_CHAIN_ID, 10)
  : 43113;

export const CHAIN_CONFIG = {
  43113: {
    name: "Avalanche Testnet C-Chain",
    chainId: 43113,
    rpcEndPoint: "https://api.avax-test.network/ext/bc/C/rpc",
  },
  11155111: {
    name: "Ethereum Sepolia",
    chainId: 11155111,
    rpcEndPoint:
      "https://sepolia.infura.io/v3/b00745ab213841c082168b25f3218c11",
  },
  17000: {
    name: "Ethereum Holesky",
    chainId: 17000,
    rpcEndPoint:
      "https://omniscient-wispy-needle.ethereum-holesky.quiknode.pro/0029815529db64e2c36976efd69c4e0c5de4f0fd",
  },
  31337: {
    name: "HardhatNetwork",
    chainId: 31337,
    rpcEndPoint: process.env.RPC_URL || "http://localhost:8545",
  },
  31338: {
    name: "BrazilCarbon QA",
    chainId: 31338,
    rpcEndPoint: process.env.RPC_URL || "http://localhost:8545",
  },
};

export const ACTIVE_CHAIN = {
  ...(CHAIN_CONFIG[_activeChainId] || CHAIN_CONFIG[43113]),
  chainIdHex: `0x${_activeChainId.toString(16)}`,
};

export const WEB3AUTH_CHAIN_CONFIG = {
  chainNamespace: "eip155",
  chainId: ACTIVE_CHAIN.chainIdHex,
  rpcTarget: ACTIVE_CHAIN.rpcEndPoint,
  displayName: ACTIVE_CHAIN.name,
  blockExplorerUrl: "https://testnet.snowtrace.io",
  ticker: "AVAX",
  tickerName: "Avalanche",
  logo: "https://cryptologos.cc/logos/ethereum-eth-logo.png",
};

export const PIMLICO_BUNDLER_URL = `https://api.pimlico.io/v2/${ACTIVE_CHAIN.chainId}/rpc?apikey=${process.env.VUE_APP_PIMLICO_API_KEY || ""}`;


export const PROJECT_STATES = {
  0: "Feasibility assessment",
  1: "Project Design",
  2: "Monitoring",
  3: "Verification",
  4: "Registration",
  5: "Offering Inactive",
  6: "Offering Live",
  7: "Offering Paused",
  8: "Offering Cancelled",
  9: "Offering Ended",
  10: "No Offering",
  11: "Other Network",
};

export const OFFERING_STATES = {
  INACTIVE: 0,
  LIVE: 1,
  PAUSED: 2,
  CANCELLED: 3,
  ENDED: 4,
  NO_OFFERING: 5,
  OTHER_NETWORK: 6,
};

export const PROJECT_STATE_MAP = {
  0: "Inactive",
  1: "Live",
  2: "Paused",
  3: "Cancelled",
  4: "Ended",
  5: "No Offering",
  6: "Other Network",
};

export const CHALLENGE_KEY = "X-Challenge";
export const SIGNATURE_KEY = "X-Signature";

export const AUDITOR_REQUIRED_FIELDS = ["email", "full_name", "wallet_address"];
export const DEVELOPER_REQUIRED_FIELDS = ["email", "full_name", "wallet_address"];
export const REGISTER_REQUIRED_FIELDS = [
  "business_name",
  "business_incorporation_date",
  "email",
  "full_name",
  "wallet_address",
];

export const MAX_COMMISSION = 100;

export const LANGUAGES = [
  { code: "pt", name: "Portuguese", flag: flagBR },
  { code: "en", name: "English", flag: flagEN },
  { code: "ja", name: "Japanese", flag: flagJA },
];

export const LIMIT = 10;

export const US_DATE_TIME_FORMAT = "%Y-%m-%d %H-%M-%S";

export const ADMIN_ROLES = {
  ADMIN: "ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN",
};

export const ADMIN_LABEL_ROLES = {
  ADMIN: "Admin",
  SUPER_ADMIN: "Super Admin",
};

export const CONTRACT_GROUPE = {
  Admin: 0,
  Owner: 1,
  Auditor: 2,
  Developer: 3,
};

export const ACTIVITY_ICONS = [
  { name: "tree", icon: tree },
  { name: "star", icon: star },
  { name: "fire", icon: fire },
  { name: "shield", icon: shield },
  { name: "smile", icon: smile },
  { name: "wheat", icon: wheat },
  { name: "leaves", icon: leaves },
  { name: "drop", icon: drop },
];

export const IMPACT_ICONS = [
  { name: "carbon-sequestration", icon: carbonSeq },
  { name: "fauna", icon: fauna },
  { name: "jobs-created", icon: jobsCreated },
  { name: "water-availability", icon: waterAvailability },
  { name: "soil-life", icon: soilLife },
  { name: "crop-diversity", icon: cropDiversity },
  { name: "conversion-land", icon: conversionLand },
  { name: "regenerative-food", icon: regenerativeFood },
  { name: "river-bank", icon: riverBank },
];

const SPECIES_ICON_BY_KEY = {
  acai,
  banana,
  black_papper,
  brazil_nuts,
  cacava,
  carbon,
  cocoa,
  coconut,
  coffee,
  cupuacu,
  lime,
  mango,
  orange,
  papaya,
  usdc,
  wood,
};

export const SPECIES_CATALOG = SPECIES_CATALOG_METADATA.map((species) => ({
  ...species,
  icon: SPECIES_ICON_BY_KEY[species.iconKey] || wood,
}));

export const SPECIES_ICONS = [
  ...SPECIES_CATALOG.flatMap((species) =>
    getSpeciesAliases(species).map((name) => ({
      name,
      key: species.key,
      iconKey: species.iconKey,
      labels: species.labels,
      icon: species.icon,
    }))
  ),
  {
    name: "carbon",
    key: "carbon",
    iconKey: "carbon",
    labels: { en: "Carbon", pt: "Carbono", ja: "炭素" },
    icon: carbon,
  },
  {
    name: "usdc",
    key: "usdc",
    iconKey: "usdc",
    labels: { en: "USDC", pt: "USDC", ja: "USDC" },
    icon: usdc,
  },
];

// Canonical-key -> icon map (carbon/usdc are not species but share the lookup).
const SPECIES_ICON_BY_CANONICAL_KEY = SPECIES_ICONS.reduce((acc, entry) => {
  if (entry.key && !acc[entry.key]) acc[entry.key] = entry.icon;
  return acc;
}, {});

// Resolve a species icon by ANY alias/locale name (e.g. "Açaí", "açaí",
// "acai", "Cacau", "cocoa") by routing through the canonical species key.
// Falls back to the `wood` icon so the UI never renders a missing image.
export const getSpeciesIcon = (name) => {
  const inputKey = normalizeSpeciesInputKey(name);
  if (!inputKey) return wood;
  const canonicalKey = SPECIES_KEY_ALIASES[inputKey] || inputKey;
  return (
    SPECIES_ICON_BY_CANONICAL_KEY[canonicalKey] ||
    SPECIES_ICON_BY_KEY[canonicalKey] ||
    wood
  );
};

export const WALLET_LOGOS = {
  metamask: metamaskIcon,
  brave: braveIcon,
  coinbase: coinbaseIcon,
  trust: trustIcon,
  rabby: rabbyIcon,
  phantom: phantomIcon,
  argent: argentIcon,
  atomic: atomicIcon,
  binance: binanceIcon,
  exodus: exodusIcon,
  okx: okxIcon,
};

// export const VERSIONS_LOGIC_ADDRESSES = {
//   "1.0.0": {
//     privateOffering: "0x21d186Fb470524B51Ab289C2864eb9069641aB9b",
//     publicOffering: "0xA2a16f66fc77235850c63E31A491b930ed59a4b2",
//     investmentContract: "0x5dc918865228d78C888b9958a5b98D09Bc696df7",
//   },
//   "2.0.0": {
//     privateOffering: "0x54111c03a8C33313c084490c082BAa6fe451d311",
//     publicOffering: "0x5749920F2548c100C5F6bF4A84f52d8684F12Ea5",
//     investmentContract: "0x61619572376AcFe705A1Df7977DF1255208B040B",
//   },
//   "3.0.0": {
//     privateOffering: "0x7fb5eD3387E40821ed2BD770e2fBc17622617127",
//     publicOffering: "0xD2b0a7C40cAea62071c3df4B3bFECc7EaE15932D",
//     investmentContract: "0x9C677F1fc649E1A1bfdFEb3CEa349F93573D5292",
//   },
//   "4.0.0": {
//     privateOffering: "0xCf7Ed3AccA5a467e9e704C703E8D87F634fB0Fc9",
//     publicOffering: "0x35E9656995E5f6209875DEfB38675e9a70D93e08",
//     investmentContract: "0x4e4AeE481EFB3F4822A728f7fdF27A485E78B65F",
//     agroInvestContract: "0xC3AD293Ca61e22D0eDD6b3DaBeDD9a91a07830Eb",
//   },
//   "5.0.0": {
//     privateOffering: "0x02dd057556fb4Aa11841f982c955177CA4784FB5",
//     publicOffering: "0xD11453084446921F4fb8FB8Bc05f863805FC2407",
//     investmentContract: "0x2702f287439EdEBe7Df632f9b3D3F4dF20a1f969",
//     agroInvestContract: "0xD37e4b725B7eD2de8becFD5fb38E68D59De74765",
//   },
// };

export const VERSIONS_LOGIC_ADDRESSES = {
  "1.0.0": {
    publicSale: "0x9220fC171F8D78C2335635f6e0Ee77E1A83AF213",
  },
};

export const LAST_VERSION = process.env.VUE_APP_CONTRACT_VERSION || "1.0.0";

export const EMISSION_ADDR = "0xed154D902793326855A755937A8D74609fD7c8bd";

export const TRANSACTION_ACTIONS = {
  INVEST: "invest",
  CLAIM: "claim",
  BUY: "buy",
  SELL: "sell",
};

export const DEFAULT_DESCRIPTIONS = {
  DESIGN:
    "Draft the project’s blueprint—boundary, baseline deforestation model, carbon stock, and community benefit plan—ensuring assumptions can survive future audits.",
  VALIDATION:
    "An accredited verifier cross-checks that blueprint against the chosen REDD+ methodology and risk tools, flagging any inflated baselines before credits can be claimed.",
  MONITORING:
    "Throughout the crediting period, you collect satellite, field-plot, and socio-economic data to prove the forest is still standing and leakage is under control.",
  VERIFICATION:
    "At set intervals, an independent auditor reviews your monitoring evidence (often with a field spot-check) to confirm the actual emission reductions achieved.",
  ISSUANCE:
    "Once the registry approves the auditor’s report, serialized carbon credits are minted—minus any buffer deduction—ready for sale, retirement, or tokenization.",
};
